import { ListFilter } from 'lucide-react';

import {
    type AppliedFilters,
    type ChoiceFilterField,
    filterText,
    type FilterGroup,
    type FilterOption,
    rangeEnds,
} from '@/components/FilterBar/filters';
import type { LookbookListQuery, LookbookOptions } from '@/features/lookbooks/api';
import { COPY } from '@/features/lookbooks/copy';
import {
    LOOKBOOK_ACTIVE,
    LOOKBOOK_FAMILY,
    type LookbookActiveFilter,
    type LookbookFamily,
} from '@/features/lookbooks/family';

/*
  The lookbook register's nine filters (ALS-76), as one group: the drawer draws a single group's
  row as a fixed heading over its fields, the Orders and Users drawer header above it (Paola's
  review of 24 Sep 2026, which supersedes the prototype's side-panel header). The fields are the
  prototype's five in its order (Category, Active, Customer, Created by, Effective), then the four
  the ticket adds (Type, Colour group, Tag, Revised by). The keys are the URL's. Filtering happens
  on the server, so this module owns the drawer's fields and the translation of the applied set
  into the API's query.
*/

export const LOOKBOOK_FILTER_KEYS = {
    type: 'type',
    category: 'category',
    client: 'client',
    colour: 'colour',
    active: 'active',
    tag: 'tag',
    createdBy: 'createdBy',
    revisedBy: 'revisedBy',
    effective: 'effective',
} as const;

export type LookbookFilterKey = (typeof LOOKBOOK_FILTER_KEYS)[keyof typeof LOOKBOOK_FILTER_KEYS];

/** The value every choice filter uses for "not applied". */
export const ANY = LOOKBOOK_ACTIVE.all;

/**
 * What the register opens on, and what Clear all goes back to: active books only, as the legacy
 * grid opened (IsActive = 1, with "--All--" and "No" offered) and as the design draws it,
 * "Active · Yes" (ALS-289). Active is a real filter here, so its chip shows from the start and
 * removing it shows every book.
 */
export const LOOKBOOK_OPENING_FILTERS: AppliedFilters<LookbookFilterKey> = {
    [LOOKBOOK_FILTER_KEYS.active]: LOOKBOOK_ACTIVE.yes,
};

/** A list longer than this is searched rather than scrolled, as the prototype's selects are. */
const SEARCH_ABOVE = 8;

/** The four families a book can be built as; Unknown is a legacy row's, never a filter. */
const FAMILIES = [
    LOOKBOOK_FAMILY.pages,
    LOOKBOOK_FAMILY.photos,
    LOOKBOOK_FAMILY.video,
    LOOKBOOK_FAMILY.products,
] as const satisfies readonly LookbookFamily[];

const ACTIVE_CHOICES: readonly LookbookActiveFilter[] = [LOOKBOOK_ACTIVE.yes, LOOKBOOK_ACTIVE.no];

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

const F = COPY.filters;

function choice(
    key: LookbookFilterKey,
    label: string,
    options: readonly FilterOption[],
): ChoiceFilterField<LookbookFilterKey> {
    return {
        key,
        kind: 'choice',
        label,
        chipLabel: label,
        neutral: ANY,
        options: [{ value: ANY, label: F.all }, ...options],
        searchable: options.length > SEARCH_ABOVE,
    };
}

const named = (rows: readonly { id: number; name: string }[]): FilterOption[] =>
    rows.map((row) => ({ value: String(row.id), label: row.name }));

/** The drawer's one group of fields, drawn from the options (GET /api/lookbooks/options). */
export function lookbookFilterGroups(lookups: LookbookOptions): FilterGroup<LookbookFilterKey>[] {
    const K = LOOKBOOK_FILTER_KEYS;
    return [
        {
            key: 'lookbooks',
            // One group, so the drawer draws its row as a fixed heading (Paola, 24 Sep 2026).
            icon: ListFilter,
            title: F.groupTitle,
            subtitle: F.groupSubtitle,
            fields: [
                choice(K.category, F.category, named(lookups.categories)),
                choice(
                    K.active,
                    F.active,
                    ACTIVE_CHOICES.map((value) => ({ value, label: value })),
                ),
                choice(
                    K.client,
                    F.client,
                    lookups.bookClients.map((c) => ({ value: String(c.userId), label: c.name })),
                ),
                choice(K.createdBy, F.createdBy, named(lookups.creators)),
                // One range field over the two days, as the prototype draws it and orders does.
                { key: K.effective, kind: 'daterange', label: F.effective, chipLabel: F.effective },
                choice(
                    K.type,
                    F.type,
                    FAMILIES.map((family) => ({ value: family, label: F.families[family] })),
                ),
                // Groups, never raw colours: a stylist looks for "the neutrals", not one of 400 shades.
                choice(K.colour, F.colour, named(lookups.colourGroups)),
                choice(K.tag, F.tag, named(lookups.tags)),
                choice(K.revisedBy, F.revisedBy, named(lookups.revisers)),
            ],
        },
    ];
}

/** The applied set with one filter back at its neutral value, as removing its chip leaves it. */
export function removeLookbookFilter(
    applied: AppliedFilters<LookbookFilterKey>,
    key: LookbookFilterKey,
): AppliedFilters<LookbookFilterKey> {
    // Neutral is absence: a removed filter is dropped, never written back as '' (the prototype's fault).
    const next = { ...applied };
    delete next[key];
    return next;
}

function id(value: string | undefined): number | undefined {
    if (value === undefined || !/^\d+$/.test(value)) return undefined;
    const n = Number(value);
    return n > 0 ? n : undefined;
}

function oneOf<T extends string>(value: string | undefined, allowed: readonly T[]): T | undefined {
    return (allowed as readonly string[]).includes(value ?? '') ? (value as T) : undefined;
}

function isoDay(value: string | undefined): string | undefined {
    const trimmed = value?.trim();
    return trimmed && ISO_DAY.test(trimmed) ? trimmed : undefined;
}

/** The applied set, the search and the paging as the API takes them. */
export function lookbookListQuery(
    applied: AppliedFilters<LookbookFilterKey>,
    q: string,
    page: number,
    pageSize: number,
): LookbookListQuery {
    const K = LOOKBOOK_FILTER_KEYS;
    const text = (key: LookbookFilterKey) => filterText(applied, key);
    // The one range control, split into the two days the API takes.
    const effective = rangeEnds(text(K.effective));
    return {
        q: q.trim() || undefined,
        family: oneOf(text(K.type), FAMILIES),
        categoryId: id(text(K.category)),
        clientUserId: id(text(K.client)),
        colourGroupId: id(text(K.colour)),
        active: oneOf(text(K.active), ACTIVE_CHOICES),
        tagId: id(text(K.tag)),
        createdBy: id(text(K.createdBy)),
        revisedBy: id(text(K.revisedBy)),
        from: isoDay(effective.from),
        to: isoDay(effective.to),
        page,
        pageSize,
    };
}
